import {
  Stepper,
  StepperProvider,
  useStepper,
} from '@/components/Molecules/Stepper'
import { buildPath } from '@/routes'
import { PropsWithChildren, useMemo } from 'react'
import { useRenewal } from '../../hooks/renewal-provider'

const RenewalStepList = {
  Vehicles: {
    id: 0,
    path: (businessEntityId: string) =>
      buildPath('/companies/:businessEntityId/certificate/renewal', {
        businessEntityId,
      }),
    title: 'Select Vehicles',
    description: 'Choose vehicles for renewal',
  },
  RegistrationPeriod: {
    id: 1,
    path: (businessEntityId: string) =>
      buildPath(
        '/companies/:businessEntityId/certificate/renewal/registration-period',
        { businessEntityId }
      ),
    title: 'Registration Period',
    description: 'Select renewal duration',
  },
  Confirm: {
    id: 2,
    path: (businessEntityId: string) =>
      buildPath('/companies/:businessEntityId/certificate/renewal/confirm', {
        businessEntityId,
      }),
    title: 'Submit Renewal',
    description: 'Review, pay, and submit',
  },
} as const

const ReRegistrationStepList = {
  Vehicles: {
    id: 0,
    path: (businessEntityId: string) =>
      buildPath('/companies/:businessEntityId/certificate/re-register', {
        businessEntityId,
      }),
    title: 'Select Vehicles',
    description: 'Choose vehicles for re-registration',
  },
  RegistrationPeriod: {
    id: 1,
    path: (businessEntityId: string) =>
      buildPath(
        '/companies/:businessEntityId/certificate/re-register/registration-period',
        { businessEntityId }
      ),
    title: 'Registration Period',
    description: 'Select re-registration duration',
  },
  Confirm: {
    id: 2,
    path: (businessEntityId: string) =>
      buildPath(
        '/companies/:businessEntityId/certificate/re-register/confirm',
        { businessEntityId }
      ),
    title: 'Submit Re-Registration',
    description: 'Review, pay, and submit',
  },
} as const

interface RenewalStepsProps {
  businessEntityId: string
}

export function RenewalSteps({
  businessEntityId,
  children,
}: PropsWithChildren<RenewalStepsProps>) {
  const { isReRegistration, isWithinCurrentRegistrationPeriod } = useRenewal()
  const skipRegistrationPeriod =
    isReRegistration && isWithinCurrentRegistrationPeriod
  const orderedSteps = useMemo(
    () =>
      Object.values(
        resolveSteps(
          isReRegistration ? ReRegistrationStepList : RenewalStepList,
          businessEntityId,
          skipRegistrationPeriod
        )
      ).sort((a, b) => a.id - b.id),
    [businessEntityId, isReRegistration, skipRegistrationPeriod]
  )

  return (
    <StepperProvider steps={orderedSteps}>
      <Stepper
        allComplete={buildPath(
          isReRegistration
            ? '/companies/:businessEntityId/certificate/re-register/success'
            : '/companies/:businessEntityId/certificate/renewal/success',
          { businessEntityId }
        )}
      />
      {children}
    </StepperProvider>
  )
}

type ResolvedSteps<T extends Record<string, { path: (id: string) => string }>> =
  {
    [K in keyof T]: Omit<T[K], 'path'> & { path: string }
  }

const resolveSteps = <
  T extends Record<string, { path: (id: string) => string }>,
>(
  steps: T,
  businessEntityId: string,
  skipRegistrationPeriod: boolean
): ResolvedSteps<T> => {
  const out = {} as ResolvedSteps<T>
  for (const key in steps) {
    const step = steps[key]
    if (skipRegistrationPeriod && key === 'RegistrationPeriod') continue
    out[key] = { ...step, path: step.path(businessEntityId) }
  }
  return out
}

export function useRenewalStepper(businessEntityId?: string) {
  const { isReRegistration, isWithinCurrentRegistrationPeriod } = useRenewal()
  const resolved = resolveSteps(
    isReRegistration ? ReRegistrationStepList : RenewalStepList,
    businessEntityId || '',
    isReRegistration && isWithinCurrentRegistrationPeriod
  )
  const orderedSteps = Object.values(resolved).sort((a, b) => a.id - b.id)

  return {
    Steps: resolved,
    ...useStepper<typeof orderedSteps>(),
  }
}
